<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay">
      <div class="modal-content">
        <div class="welcome-section">
          <h2>{{ $t('language.welcome') }}</h2>
          <p class="welcome-text">{{ $t('language.select_prompt') }}</p>
        </div>
        
        <div class="language-selector">
          <select 
            v-model="selectedLanguage" 
            class="language-select"
          >
            <option value="zh">中文</option>
            <option value="en">English</option>
            <option value="fr">Français</option>
            <option value="de">Deutsch</option>
            <option value="nl">Nederlands</option>
            <option value="es">Español</option>
          </select>
        </div>

        <button @click="confirmLanguage" class="confirm-button">
          {{ $t('language.confirm') }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { setLocale } from '../i18n'

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close'])
const { locale } = useI18n()

// 获取系统语言
const userLanguage = navigator.language.toLowerCase()
const systemLanguage = (() => {
  if (userLanguage.startsWith('zh')) return 'zh'
  if (userLanguage.startsWith('fr')) return 'fr'
  if (userLanguage.startsWith('de')) return 'de'
  if (userLanguage.startsWith('nl')) return 'nl'
  if (userLanguage.startsWith('es')) return 'es'
  return 'en'
})()

const selectedLanguage = ref(systemLanguage)

const confirmLanguage = () => {
  setLocale(selectedLanguage.value)
  localStorage.setItem('language_selected', 'true')
  emit('close')
}

onMounted(() => {
  selectedLanguage.value = systemLanguage
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-color);
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  max-width: 480px;
  width: 90%;
  text-align: center;
}

.welcome-section {
  margin-bottom: 2rem;
}

.welcome-section h2 {
  font-size: 2rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.welcome-text {
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.5;
}

.language-selector {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
}

.language-select {
  appearance: none;
  background-color: var(--bg-color);
  border: 2px solid var(--border-color);
  border-radius: var(--radius);
  color: var(--text-color);
  cursor: pointer;
  font-size: 1.1rem;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  width: 200px;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1em;
}

.language-select:hover {
  border-color: var(--primary-color);
}

.language-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px var(--primary-light);
}

.language-select option {
  padding: 0.5rem;
}

.confirm-button {
  padding: 0.75rem 2rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
  min-width: 120px;
  font-size: 1.1rem;
}

.confirm-button:hover {
  background: var(--primary-dark);
}

@media (max-width: 480px) {
  .modal-content {
    padding: 1.5rem;
  }

  .welcome-section h2 {
    font-size: 1.5rem;
  }

  .welcome-text {
    font-size: 1rem;
  }

  .language-select {
    font-size: 1rem;
    padding: 0.6rem 2rem 0.6rem 0.8rem;
    width: 180px;
  }
}
</style> 